'use client';

import { c as _c } from "react/compiler-runtime";
import { CC_PAID_ROUNDUP_AO_PAGE_NAME, CC_ROUNDUP_SEGMENT_PAGE_NAME, TrafficOriginType, type TrafficOriginTypeProps } from '@cc-shopping/contracts/credit-cards-page/constants';
import type { PreComputedFilterOfferIds } from '@cc-shopping/contracts/credit-cards-page/roundup/types';
import { ABSmartlyProvider } from '@cc-shopping/platform-absmartly/context/ABSmartlyProvider';
import type { ABSmartlyContextData } from '@cc-shopping/platform-absmartly/types/absmartly';
import type { ABTestVariantResult } from '@cc-shopping/platform-absmartly/utils/variants';
import { AnalyticsPageView as SegmentPageView } from '@cc-shopping/platform-analytics/AnalyticsPageView';
import { AcxiomDataCollection } from '@cc-shopping/sequencing/client/AcxiomDataCollection';
import { NerdWalletThemeProvider } from '@cc-shopping/ui/composites/NerdWalletThemeProvider';
import { CssBaseline } from '@mui/material';
import type { PropsWithChildren } from 'react';
import { ApprovalLikelihoodProvider } from 'src/features/approval-odds/context';
import { CompareBarProvider } from 'src/features/compare-bar/CompareBar/Context';
import type { ComparisonCreditCardOffer } from 'src/features/compare-bar/CompareBar/types';
import { WPAdminFeatures } from 'src/features/content/WPAdminFeatures';
import { FiltersDialogContextProvider } from 'src/features/roundup-filters/Mobile/components/FiltersDialogContext';
import { OffersFilterStoreProvider } from 'src/features/roundup-filters/store/Context';
import { CompassPageViewRegistration } from 'src/shared/analytics/CompassPageViewRegistration';
export type RoundupClientProvidersProps = {
  abSmartlyContextData?: ABSmartlyContextData;
  shouldEnableRoundupFilters?: boolean;
  staticExperimentResult: ABTestVariantResult;
  slug: string | undefined;
  id: string;
  preComputedFilterOfferIds: PreComputedFilterOfferIds;
  compareBarOffers: ComparisonCreditCardOffer[];
  showApprovalOddsActivationBanner?: boolean;
} & TrafficOriginTypeProps;
function TrafficOriginTypeToggledProviders(t0) {
  const $ = _c(3);
  const {
    children,
    trafficOriginType
  } = t0;
  const t1 = trafficOriginType !== TrafficOriginType.Paid;
  let t2;
  if ($[0] !== children || $[1] !== t1) {
    t2 = <ApprovalLikelihoodProvider shouldFetchOnMount={t1}>{children}</ApprovalLikelihoodProvider>;
    $[0] = children;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
export function RoundupClientProviders(t0) {
  const $ = _c(27);
  const {
    abSmartlyContextData,
    children,
    compareBarOffers,
    id,
    preComputedFilterOfferIds,
    slug,
    shouldEnableRoundupFilters: t1,
    staticExperimentResult,
    showApprovalOddsActivationBanner: t2,
    trafficOriginType
  } = t0;
  const shouldEnableRoundupFilters = t1 === undefined ? false : t1;
  const showApprovalOddsActivationBanner = t2 === undefined ? false : t2;
  const pageName = trafficOriginType === TrafficOriginType.Paid && showApprovalOddsActivationBanner ? CC_PAID_ROUNDUP_AO_PAGE_NAME : CC_ROUNDUP_SEGMENT_PAGE_NAME[trafficOriginType];
  let t3;
  if ($[0] !== slug) {
    t3 = <AcxiomDataCollection slug={slug} />;
    $[0] = slug;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <CssBaseline />;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== id) {
    t5 = <WPAdminFeatures id={id} name="Roundup" />;
    $[3] = id;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== children || $[6] !== t3 || $[7] !== t5) {
    t6 = <FiltersDialogContextProvider>{t3}{t4}{t5}{children}</FiltersDialogContextProvider>;
    $[5] = children;
    $[6] = t3;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== preComputedFilterOfferIds || $[10] !== shouldEnableRoundupFilters || $[11] !== t6) {
    t7 = <OffersFilterStoreProvider areFiltersEnabled={shouldEnableRoundupFilters} preComputedFilterOfferIds={preComputedFilterOfferIds}>{t6}</OffersFilterStoreProvider>;
    $[9] = preComputedFilterOfferIds;
    $[10] = shouldEnableRoundupFilters;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t7 || $[14] !== trafficOriginType) {
    t8 = <CompassPageViewRegistration><NerdWalletThemeProvider><TrafficOriginTypeToggledProviders trafficOriginType={trafficOriginType}>{t7}</TrafficOriginTypeToggledProviders></NerdWalletThemeProvider></CompassPageViewRegistration>;
    $[13] = t7;
    $[14] = trafficOriginType;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== id || $[17] !== pageName || $[18] !== t8) {
    t9 = <SegmentPageView name={pageName} contentId={id} contentSource="wp">{t8}</SegmentPageView>;
    $[16] = id;
    $[17] = pageName;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== compareBarOffers || $[21] !== t9) {
    t10 = <CompareBarProvider allOffers={compareBarOffers}>{t9}</CompareBarProvider>;
    $[20] = compareBarOffers;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  const childrenProviders = t10;
  let t11;
  if ($[23] !== abSmartlyContextData || $[24] !== childrenProviders || $[25] !== staticExperimentResult) {
    t11 = abSmartlyContextData ? <ABSmartlyProvider contextData={abSmartlyContextData} staticExperimentResult={staticExperimentResult}>{childrenProviders}</ABSmartlyProvider> : childrenProviders;
    $[23] = abSmartlyContextData;
    $[24] = childrenProviders;
    $[25] = staticExperimentResult;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  return t11;
}